import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Steps } from "primereact/steps";
import { MenuItem, MenuItemOptions } from "primereact/menuitem";
import { Button } from "primereact/button";
import { InputOtp } from "primereact/inputotp";
import { Messages } from "primereact/messages";
import { ProgressSpinner } from "primereact/progressspinner";
import Container from "../../components/menu/Container";
import getUserChaveDuploFator, {
  ChaveDuploFatorResponse,
} from "../../service/getUserChaveDuploFator";
import postUserValidarDuploFator from "../../service/postUserValidarDuploFator";
import deleteUserDuploFator from "../../service/deleteUserDuploFator";
import "./AutenticacaoDoisFatores.css";

const AZUL = "#183462";

// template que troca o número do step por um ícone, reutilizando as classes do tema
const stepTemplate =
  (icon: string) => (item: MenuItem, options: MenuItemOptions) =>
    (
      <span
        className="p-menuitem-link"
        role="button"
        tabIndex={0}
        onClick={options.onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") options.onClick(e as any);
        }}
        style={{ cursor: "pointer" }}
      >
        <span className="p-steps-number">
          <i className={icon}></i>
        </span>
        <span className="p-steps-title">{item.label}</span>
      </span>
    );

// normaliza o retorno do backend para uma data URI de imagem exibível no <img>
const resolveQrCodeSrc = (chave?: ChaveDuploFatorResponse): string => {
  const raw = chave?.qrCode;
  if (!raw) return "";
  if (raw.startsWith("data:")) return raw;
  return `data:image/png;base64,${raw}`;
};

export default function Page() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const msgs = useRef<Messages>(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [chave, setChave] = useState<ChaveDuploFatorResponse | null>(null);
  const [codigo, setCodigo] = useState("");
  const [validando, setValidando] = useState(false);
  const [removendo, setRemovendo] = useState(false);

  const items: MenuItem[] = [
    { label: String(t("escanearQrCode")), template: stepTemplate("pi pi-qrcode") },
    { label: String(t("validarCodigo")), template: stepTemplate("pi pi-check-circle") },
  ];

  const carregarChave = () => {
    setLoading(true);
    getUserChaveDuploFator()
      .then((data) => setChave(data))
      .catch((error) => {
        msgs.current?.clear();
        msgs.current?.show([
          {
            sticky: true,
            severity: "error",
            summary: t("erro"),
            detail: error.message,
            closable: false,
          },
        ]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    carregarChave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validar = () => {
    setValidando(true);
    postUserValidarDuploFator(codigo)
      .then((data) => {
        msgs.current?.clear();
        msgs.current?.show([
          {
            sticky: false,
            life: 6000,
            severity: "success",
            summary: t("sucesso"),
            detail: data || t("duploFatorConfiguradoSucesso"),
            closable: false,
          },
        ]);
        setTimeout(() => navigate("/home"), 1500);
      })
      .catch((error) => {
        msgs.current?.clear();
        msgs.current?.show([
          {
            sticky: false,
            life: 8000,
            severity: "error",
            summary: t("erro"),
            detail: error.message,
            closable: false,
          },
        ]);
      })
      .finally(() => setValidando(false));
  };

  const remover = () => {
    if (!window.confirm(String(t("duploFatorRemoverConfirmacao")))) return;
    setRemovendo(true);
    deleteUserDuploFator()
      .then(() => {
        msgs.current?.clear();
        msgs.current?.show([
          {
            sticky: false,
            life: 6000,
            severity: "success",
            summary: t("sucesso"),
            detail: t("duploFatorRemovidoSucesso"),
            closable: false,
          },
        ]);
        // recarrega o estado: sem duplo fator configurado, volta para o fluxo de setup
        setActiveIndex(0);
        setCodigo("");
        carregarChave();
      })
      .catch((error) => {
        msgs.current?.clear();
        msgs.current?.show([
          {
            sticky: false,
            life: 8000,
            severity: "error",
            summary: t("erro"),
            detail: error.message,
            closable: false,
          },
        ]);
      })
      .finally(() => setRemovendo(false));
  };

  const qrCodeSrc = resolveQrCodeSrc(chave ?? undefined);
  const jaConfigurado = !!chave?.jaConfigurado;

  return (
    <Container>
      <div className="row">
        <div className="col-lg-12 mb-3">
          <div className="d-sm-flex align-items-center justify-content-between mb-4">
            <h1 className="h3 mb-0 text-gray">
              <i className="pi pi-shield" style={{ fontSize: "2.0rem" }}></i>{" "}
              {t("autenticacaoDoisFatores")}
            </h1>
          </div>
        </div>
      </div>

      <div className="card">
        <Messages ref={msgs} />

        {jaConfigurado && (
          <div
            className="flex-consulta flex-column align-items-center text-center"
            style={{ padding: "2rem 1rem" }}
          >
            <i
              className="pi pi-check-circle"
              style={{ fontSize: "3.5rem", color: "#22c55e", marginBottom: "1rem" }}
            />
            <h2 style={{ margin: 0, color: "#333" }}>
              {t("duploFatorJaConfiguradoTitulo")}
            </h2>
            <p style={{ maxWidth: "520px", color: "#555", marginTop: "0.5rem" }}>
              {t("duploFatorJaConfiguradoMensagem")}
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "1rem",
                flexWrap: "wrap",
                marginTop: "1.5rem",
              }}
            >
              <Button
                label={String(t("voltarParaInicio"))}
                icon="pi pi-home"
                style={{ backgroundColor: AZUL }}
                onClick={() => navigate("/home")}
              />
              <Button
                label={String(t("removerDuploFator"))}
                icon="pi pi-trash"
                severity="danger"
                outlined
                loading={removendo}
                onClick={remover}
              />
            </div>
          </div>
        )}

        {!jaConfigurado && (
          <Steps
            model={items}
            activeIndex={activeIndex}
            readOnly
            className="steps-2fa mb-5"
            style={{ marginBottom: "2rem" }}
          />
        )}

        {!jaConfigurado && activeIndex === 0 && (
          <div className="flex-consulta flex-column align-items-center text-center">
            <p style={{ maxWidth: "520px", color: "#555" }}>
              {t("duploFatorInstrucaoQr")}
            </p>

            {loading ? (
              <div style={{ padding: "3rem" }}>
                <ProgressSpinner style={{ width: "60px", height: "60px" }} />
              </div>
            ) : qrCodeSrc ? (
              <img
                src={qrCodeSrc}
                alt="QR Code"
                style={{
                  width: "220px",
                  height: "220px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "8px",
                }}
              />
            ) : (
              <p className="p-error">{t("duploFatorQrIndisponivel")}</p>
            )}

            {chave?.chaveSecreta && (
              <div style={{ marginTop: "1rem" }}>
                <small style={{ color: "#555" }}>
                  {t("duploFatorChaveManual")}
                </small>
                <div
                  style={{
                    fontFamily: "monospace",
                    fontSize: "1.1rem",
                    letterSpacing: "2px",
                    marginTop: "4px",
                    fontWeight: "bold",
                    color: AZUL,
                  }}
                >
                  {chave.chaveSecreta}
                </div>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
                width: "100%",
                marginTop: "2.5rem",
              }}
            >
              <Button
                label={String(t("cancelar"))}
                severity="secondary"
                outlined
                onClick={() => navigate("/home")}
              />
              <Button
                label={String(t("prosseguir"))}
                icon="pi pi-arrow-right"
                iconPos="right"
                style={{ backgroundColor: AZUL }}
                disabled={loading || !qrCodeSrc}
                onClick={() => setActiveIndex(1)}
              />
            </div>
          </div>
        )}

        {!jaConfigurado && activeIndex === 1 && (
          <div className="flex-consulta flex-column align-items-center text-center">
            <p style={{ maxWidth: "520px", color: "#555" }}>
              {t("duploFatorInstrucaoCodigo")}
            </p>

            <InputOtp
              value={codigo}
              onChange={(e) => setCodigo(String(e.value ?? ""))}
              length={6}
              integerOnly
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
                width: "100%",
                marginTop: "2.5rem",
              }}
            >
              
              <Button
                label={String(t("voltar"))}
                icon="pi pi-arrow-left"
                severity="secondary" outlined
                onClick={() => setActiveIndex(0)}
              />
              <Button
                label={String(t("validar"))}
                icon="pi pi-check"
                iconPos="right"
                style={{ backgroundColor: AZUL }}
                loading={validando}
                disabled={codigo.length < 6}
                onClick={validar}
              />
            </div>
          </div>
        )}
      </div>
    </Container>
  );
}
